import React, { useEffect, useState } from "react";
import { PropTypes } from "prop-types";
import { generatePath, useLocation, useNavigate } from "react-router-dom";
import { getTunnelHmPayload } from "../../../../../../config/reqConfig/anomalyDetection";
import ApiService from "../../../../../../config/api-config";
import {
  backtoSevenDays,
  displayDateTime,
} from "../../../../../../utils/displayTime";
import { colors } from "../../../../../../utils/colors";
import { getConfig, getCellElem } from "../../common/heatmapConfig";
import Spinner from "../../../../../../common/Spinner";
import ErrorComponent from "../../../../../../common/ErrorComponent";
import NoDataAvailable from "../../../../../../common/NoDataAvailable";
import CustomTimeStamp from "../../../../../../generics/TimeStamp/customTimeStamp";
import reactWrapper from "@harbor/elements/utils/react/wrapper";
import css from "../../common/anomalyMagneticStyle.less";
import AppHoc from "../../../../../../generics/AppHOC";
import SingleSiteTrendCharts from "./SingleSiteTrendCharts";
import { format } from "date-fns";
import * as actions from "../../actions";
import { connect } from "react-redux";
import { bindActionCreators } from "@reduxjs/toolkit";
import { convertToCSV } from "../../common/adUtils";
import useI18n from "../../../../../../i18n/useI18n";
import { routeURLs } from "../../../../../../utils/enums";

const mapStateToProps = (state) => {
  return {
    adKey: state.vanalytics.adKey,
  };
};

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(actions, dispatch),
});
const [
  HbrHeatmapChart,
  HbrBreadcrumb,
  HbrBreadcrumbItem,
  HbrCard,
  HbrTooltip,
  HbrIconButton,
] = reactWrapper([
  "hbr-heatmap-chart",
  "hbr-breadcrumb",
  "hbr-breadcrumb-item",
  "hbr-card",
  "hbr-tooltip",
  "hbr-icon-button",
]);

const SingleSiteTrend = (props) => {
  const { tViews } = useI18n("views");
  const location = useLocation();
  const navigate = useNavigate();
  const [hmError, setHmError] = useState(null);
  const [timePeriod, setTimePeriod] = useState(null);
  const [hmLoader, setHmLoader] = useState(true);
  const [hmData, setHmData] = useState(null);
  const [selectedHour, setSelectedHour] = useState(null);
  const [tunnelName, setTunnelName] = useState(null);
  const [tunnelAttr, setTunnelAttr] = useState(null);
  const [locationAttr, setLocationAttr] = useState(null);
  const [selectedElems, setSelectedElems] = useState({
    prev: null,
    current: null,
  });

  useEffect(() => {
    if (selectedElems.current !== selectedElems.prev) {
      selectedElems.prev?.setAttribute("stroke-width", "0");
      selectedElems.prev?.setAttribute("stroke", "none");
      selectedElems.current?.setAttribute("stroke-width", "5");
      selectedElems.current?.setAttribute("stroke", colors.blue45);
    } else {
      selectedElems.current?.setAttribute("stroke-width", "0");
      selectedElems.current?.setAttribute("stroke", "none");
    }
  }, [selectedElems]);

  useEffect(() => {
    if (selectedHour) {
      let initDayLabel = format(new Date(selectedHour), "EEE d");
      let initHourLabel = format(new Date(selectedHour), "h:mm aa");

      setTimeout(() => {
        getCellElem(
          ".ss-trend-chart hbr-heatmap-chart",
          initDayLabel,
          initHourLabel,
          setSelectedElems,
        );
      }, 500);
    }
  }, [hmData]);

  useEffect(() => {
    const runEffect = async () => {
      window.scrollTo(0, 0, "smooth");
      try {
        let tunnelAttributes = JSON.parse(
          localStorage.getItem("tunnel_details"),
        );
        setTunnelAttr(tunnelAttributes);
        let locationAttributes = JSON.parse(
          localStorage.getItem("local_details"),
        );
        setLocationAttr(locationAttributes);
        setHmLoader(true);
        const tunnelNameVal = tunnelAttributes.tunnel_name;
        setTunnelName(tunnelNameVal);
        let timePeriod = backtoSevenDays();
        setTimePeriod(timePeriod);
        const tunnel_hm_payload = getTunnelHmPayload(timePeriod, tunnelNameVal);
        const res = await ApiService.getADSingleSiteTunnelHm(tunnel_hm_payload);

        if (!res.errorObject) {
          const config = getConfig(
            res,
            setSelectedHour,
            timePeriod,
            setSelectedElems,
            props.adKey,
            props.actions,
            tViews("adChartsID.trendChartID"),
          );
          setHmData(config);
        } else {
          setHmError(res.errorObject);
        }
        setHmLoader(false);
      } catch (error) {
        setHmError(error.message);
      }
    };

    runEffect();
  }, [location, props.globalFilter.onAnomalyRefresh]);

  return (
    <div
      className={css["ss-trend-view"]}
      role="region"
      aria-label={tViews("predictiveNetworks.anomalyAndUsageTrend")}
    >
      <div className="ss-trend-header">
        <div className="ss-bread-crumb">
          <HbrBreadcrumb shadowrootmode="open">
            <HbrBreadcrumbItem
              onClick={() => {
                props.setSelectedSite(-1);
                navigate(routeURLs.ad);
              }}
            >
              {tViews("predictiveNetworks.anomaly")}
            </HbrBreadcrumbItem>
            <HbrBreadcrumbItem
              onClick={() => {
                navigate(
                  generatePath(routeURLs.adSingleSite, {
                    siteName: props.globalFilter.selectedSite,
                  }),
                );
              }}
            >
              {tViews("predictiveNetworks.tunnelDetails")}
            </HbrBreadcrumbItem>
          </HbrBreadcrumb>
        </div>
        <div className="ss-trend-header-title-container">
          <div className="ss-trend-header-title">
            {tViews("predictiveNetworks.anomalyAndUsageTrend")}
          </div>
          <div id="va-ad-qualtrics-container">
            {/* The Qualtrics Thumbs Up and Thumbs Down survey widget for the AD 3rd page is rendered here */}
          </div>
        </div>
        <div className="ss-trend-attr-details">
          <div>
            <div className="hbr-css__text-primary-xs-semibold">
              {tViews("predictiveNetworks.tunnelName")}
            </div>
            <div className="hbr-css__text-primary-xs" id="va_ad_tunnel_name">
              {tunnelAttr?.tunnel_name}
            </div>
          </div>
          <div>
            <div className="hbr-css__text-primary-xs-semibold">
              {tViews("predictiveNetworks.localSite")}
            </div>
            <div className="hbr-css__text-primary-xs" id="va_ad_site_name">
              {locationAttr?.local_site}
            </div>
          </div>
          <div>
            <div className="hbr-css__text-primary-xs-semibold">
              {tViews("predictiveNetworks.remoteSite")}
            </div>
            <div
              className="hbr-css__text-primary-xs"
              id="va_ad_remote_site_name"
            >
              {tunnelAttr?.remote_site_name}
            </div>
          </div>
          <div>
            <div className="hbr-css__text-primary-xs-semibold">
              {tViews("predictiveNetworks.localLocation")}
            </div>
            <div className="hbr-css__text-primary-xs" id="va_ad_local_location">
              {locationAttr?.local_location}
            </div>
          </div>
          <div>
            <div className="hbr-css__text-primary-xs-semibold">
              {tViews("predictiveNetworks.remoteLocation")}
            </div>
            <div
              className="hbr-css__text-primary-xs"
              id="va_ad_remote_location"
            >
              {tunnelAttr?.remote_site_city}
            </div>
          </div>
        </div>
      </div>
      <HbrCard className="ad-ss-trend-widget" container={true}>
        <div aria-live="polite">
          {hmLoader ? (
            <Spinner />
          ) : hmError ? (
            <ErrorComponent />
          ) : hmData.data.length === 0 ? (
            <NoDataAvailable />
          ) : (
            <>
              <div className="custom-trend-timeFrame">
                <CustomTimeStamp
                  className="timestamp"
                  prevTime={displayDateTime(timePeriod[0])}
                  currentTime={displayDateTime(timePeriod[1])}
                />
                <div className="ad-timePeriodTxt">{`${tViews("app360.afHeatmapTimePriodText")}`}</div>
              </div>
              <div className="ss-trend-chart">
                <HbrHeatmapChart config={hmData}>
                  <div slot="header-action">
                    <HbrTooltip content={`${tViews("downLoadCsv")}`}>
                      {/* `${i18n.adSsTrendHeatmap}` */}
                      <span>
                        <HbrIconButton
                          name={`${tViews("csvFile")}`}
                          size="20px"
                          arialabel={`${tViews("downloadData")}`}
                          onClick={() =>
                            convertToCSV(
                              hmData.data,
                              `${tViews("adSsTrendHeatmap")}`,
                            )
                          }
                        />
                      </span>
                    </HbrTooltip>
                  </div>
                </HbrHeatmapChart>
              </div>
            </>
          )}
        </div>
        {tunnelName && (
          <div className="ad-ss-trend-charts-container">
            <SingleSiteTrendCharts
              selectedHour={selectedHour}
              tunnelName={tunnelName}
            />
          </div>
        )}
      </HbrCard>
    </div>
  );
};

SingleSiteTrend.propTypes = {
  globalFilter: PropTypes.object,
  setSelectedSite: PropTypes.func,
  adKey: PropTypes.object,
  actions: PropTypes.object,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(AppHoc(SingleSiteTrend));
